import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronRight, X, Check, FileText, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/PageHeader';
import {
  bowelService,
  noteService,
  type BowelLog,
  type DailyNote,
  type BowelConsistency,
  type BowelAmount,
  type NoteMood,
  consistencyLabels,
  consistencyEmoji,
  amountLabels,
  moodEmoji,
  getLocalDateString,
} from '../services/journalService';

// ============================================
// TYPES
// ============================================
type TabType = 'transit' | 'notes';

// ============================================
// ADD BOWEL LOG MODAL
// ============================================
function AddBowelModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [consistency, setConsistency] = useState<BowelConsistency>('NORMAL');
  const [amount, setAmount] = useState<BowelAmount | ''>('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const consistencies: BowelConsistency[] = ['VERY_HARD', 'HARD', 'NORMAL', 'SOFT', 'LOOSE', 'LIQUID'];
  const amounts: BowelAmount[] = ['SMALL', 'MEDIUM', 'LARGE'];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await bowelService.create({
        date: getLocalDateString(),
        time: time || undefined,
        consistency,
        amount: amount || undefined,
        notes: notes || undefined,
      });
      toast.success('Înregistrare adăugată!');
      onSuccess();
      onClose();
      setConsistency('NORMAL');
      setAmount('');
      setTime('');
      setNotes('');
    } catch {
      toast.error('Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-10 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">💩 Adaugă Tranzit</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Consistency */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">Consistență (Scala Bristol)</p>
          <div className="grid grid-cols-2 gap-2">
            {consistencies.map((c) => (
              <button
                key={c}
                onClick={() => setConsistency(c)}
                className={`flex items-center gap-2 p-3 rounded-xl border-2 text-left transition-all ${
                  consistency === c
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-lg">{consistencyEmoji[c]}</span>
                <span className="text-xs font-medium text-gray-700 leading-tight">
                  {consistencyLabels[c]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Amount */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">Cantitate (opțional)</p>
          <div className="flex gap-2">
            {amounts.map((a) => (
              <button
                key={a}
                onClick={() => setAmount(amount === a ? '' : a)}
                className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                  amount === a
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {amountLabels[a]}
              </button>
            ))}
          </div>
        </div>

        {/* Time */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-2">Ora (opțional)</p>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>

        {/* Notes */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">Notițe (opțional)</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observații, simptome asociate..."
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-purple-600 text-white py-3.5 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-5 h-5" />
              Salvează
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================
// ADD/EDIT NOTE MODAL
// ============================================
function NoteModal({
  isOpen,
  onClose,
  onSuccess,
  existingNote,
  date,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingNote: DailyNote | null;
  date: string;
}) {
  const [content, setContent] = useState(existingNote?.content || '');
  const [mood, setMood] = useState<NoteMood | ''>(existingNote?.mood || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setContent(existingNote?.content || '');
      setMood(existingNote?.mood || '');
    }
  }, [isOpen, existingNote]);

  const moods: NoteMood[] = ['great', 'good', 'okay', 'bad', 'terrible'];

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Scrie ceva în notiță');
      return;
    }
    setLoading(true);
    try {
      await noteService.upsert({
        date,
        content: content.trim(),
        mood: mood || undefined,
      });
      toast.success('Notiță salvată!');
      onSuccess();
      onClose();
    } catch {
      toast.error('Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-10 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">
            📝 {existingNote ? 'Editează Notiță' : 'Notiță Nouă'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Mood */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">Cum ți-a fost ziua? 😊</p>
          <div className="flex gap-2 justify-between">
            {moods.map((m) => (
              <button
                key={m}
                onClick={() => setMood(mood === m ? '' : m)}
                className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-all ${
                  mood === m
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-xl">{moodEmoji[m]}</span>
                <span className="text-[10px] text-gray-500 leading-tight text-center">
                  {m === 'great' ? 'Super' : m === 'good' ? 'Bine' : m === 'okay' ? 'Ok' : m === 'bad' ? 'Rău' : 'Groaznic'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">Notiță</p>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Cum a fost ziua ta? Ce ai mâncat, cum te-ai simțit, ce ai realizat..."
            rows={6}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
            autoFocus
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{content.length} caractere</p>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading || !content.trim()}
          className="w-full bg-purple-600 text-white py-3.5 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-5 h-5" />
              Salvează Notița
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================
// BOWEL LOG CARD
// ============================================
function BowelLogCard({ log, onDelete }: { log: BowelLog; onDelete: (id: string) => void }) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center text-xl">
            {consistencyEmoji[log.consistency]}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {consistencyLabels[log.consistency]}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-400">{formatDate(log.date)}</span>
              {log.time && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-400">{log.time}</span>
                </>
              )}
              {log.amount && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-400">{amountLabels[log.amount]}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => onDelete(log.id)}
          className="p-2 rounded-xl hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {log.notes && (
        <p className="text-xs text-gray-500 mt-2 pl-13 ml-13 border-t border-gray-50 pt-2">
          {log.notes}
        </p>
      )}
    </div>
  );
}

// ============================================
// NOTE CARD
// ============================================
function NoteCard({
  note,
  onEdit,
  onDelete,
}: {
  note: DailyNote;
  onEdit: (note: DailyNote) => void;
  onDelete: (id: string) => void;
}) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  };

  return (
    <div
      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onEdit(note)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {note.mood && (
            <span className="text-xl">{moodEmoji[note.mood as NoteMood]}</span>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 capitalize">
              {formatDate(note.date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <ChevronRight className="w-4 h-4 text-gray-300" />
        </div>
      </div>
      <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
        {note.content}
      </p>
      <p className="text-xs text-gray-400 mt-2">
        {new Date(note.updatedAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  );
}

// ============================================
// MAIN PAGE
// ============================================
export default function JournalPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('transit');

  // Bowel state
  const [bowelLogs, setBowelLogs] = useState<BowelLog[]>([]);
  const [bowelTotal, setBowelTotal] = useState(0);
  const [bowelLoading, setBowelLoading] = useState(false);
  const [showAddBowel, setShowAddBowel] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<DailyNote[]>([]);
  const [notesTotal, setNotesTotal] = useState(0);
  const [notesLoading, setNotesLoading] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState<DailyNote | null>(null);
  const [todayNote, setTodayNote] = useState<DailyNote | null>(null);

  const today = getLocalDateString();

  // Load bowel logs
  const loadBowelLogs = useCallback(async () => {
    setBowelLoading(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const result = await bowelService.getAll({
        startDate: getLocalDateString(thirtyDaysAgo),
        limit: 50,
      });
      setBowelLogs(result.logs);
      setBowelTotal(result.total);
    } catch {
      console.error('Error loading bowel logs');
    } finally {
      setBowelLoading(false);
    }
  }, []);

  // Load notes
  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const [notesResult, todayNoteResult] = await Promise.all([
        noteService.getAll({ limit: 30 }),
        noteService.getToday(),
      ]);
      setNotes(notesResult.notes);
      setNotesTotal(notesResult.total);
      setTodayNote(todayNoteResult);
    } catch {
      console.error('Error loading notes');
    } finally {
      setNotesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'transit') {
      loadBowelLogs();
    } else {
      loadNotes();
    }
  }, [activeTab, loadBowelLogs, loadNotes]);

  const handleDeleteBowel = async (id: string) => {
    if (!confirm('Ștergi această înregistrare?')) return;
    try {
      await bowelService.delete(id);
      toast.success('Înregistrare ștearsă');
      loadBowelLogs();
    } catch {
      toast.error('Eroare la ștergere');
    }
  };

  const handleDeleteNote = async (id: string) => {
    if (!confirm('Ștergi această notiță?')) return;
    try {
      await noteService.delete(id);
      toast.success('Notiță ștearsă');
      loadNotes();
    } catch {
      toast.error('Eroare la ștergere');
    }
  };

  const handleEditNote = (note: DailyNote) => {
    setEditingNote(note);
    setShowNoteModal(true);
  };

  const handleAddNote = () => {
    setEditingNote(todayNote);
    setShowNoteModal(true);
  };

  // Group bowel logs by date
  const groupedBowelLogs = bowelLogs.reduce((acc, log) => {
    const dateKey = log.date.split('T')[0];
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {} as Record<string, BowelLog[]>);

  const formatGroupDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const todayDate = new Date(today);
    const diffDays = Math.round((todayDate.getTime() - date.getTime()) / 86400000);
    if (diffDays === 0) return 'Azi';
    if (diffDays === 1) return 'Ieri';
    return date.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader
        title="Jurnal"
        subtitle="Tranzit intestinal și notițe zilnice"
      />

      <div className="max-w-2xl mx-auto px-4 pt-4">

        {/* Tab Switcher */}
        <div className="bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 mb-5 flex">
          <button
            onClick={() => setActiveTab('transit')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'transit'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="text-base">💩</span>
            Tranzit
            {bowelTotal > 0 && activeTab !== 'transit' && (
              <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {bowelTotal}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'notes'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4" />
            Notițe
            {notesTotal > 0 && activeTab !== 'notes' && (
              <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {notesTotal}
              </span>
            )}
          </button>
        </div>

        {/* ==================== TRANSIT TAB ==================== */}
        {activeTab === 'transit' && (
          <div>
            {/* Info banner */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-4 mb-5 text-white">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💩</span>
                <div>
                  <p className="font-semibold text-sm">Tranzit intestinal</p>
                  <p className="text-xs text-white/80 mt-0.5">
                    Urmărește frecvența și consistența pentru o digestie sănătoasă. 
                    Ideal: 1-3 ori/zi, consistență normală (Tip 3-4).
                  </p>
                </div>
              </div>
            </div>

            {/* Add button */}
            <button
              onClick={() => setShowAddBowel(true)}
              className="w-full bg-white border-2 border-dashed border-purple-300 rounded-2xl py-4 flex items-center justify-center gap-2 text-purple-600 font-semibold text-sm hover:bg-purple-50 transition-colors mb-5"
            >
              <Plus className="w-5 h-5" />
              Adaugă înregistrare
            </button>

            {/* Logs */}
            {bowelLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : Object.keys(groupedBowelLogs).length === 0 ? (
              <div className="text-center py-16">
                <div className="text-5xl mb-4">💩</div>
                <p className="text-gray-500 font-medium">Nicio înregistrare adăugată</p>
                <p className="text-gray-400 text-sm mt-1">
                  Apasă butonul de mai sus pentru a adăuga prima înregistrare
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {Object.entries(groupedBowelLogs).map(([dateKey, logs]) => (
                  <div key={dateKey}>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {formatGroupDate(dateKey)}
                      </p>
                      <span className="text-xs text-gray-400">({logs.length}x)</span>
                    </div>
                    <div className="space-y-2">
                      {logs.map((log) => (
                        <BowelLogCard
                          key={log.id}
                          log={log}
                          onDelete={handleDeleteBowel}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== NOTES TAB ==================== */}
        {activeTab === 'notes' && (
          <div>
            {/* Today's note card */}
            <div
              className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mb-5 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleAddNote}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📝</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Notiță de azi</p>
                    <p className="text-xs text-gray-400">
                      {new Date().toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                </div>
                {todayNote?.mood && (
                  <span className="text-2xl">{moodEmoji[todayNote.mood as NoteMood]}</span>
                )}
              </div>

              {todayNote ? (
                <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
                  {todayNote.content}
                </p>
              ) : (
                <div className="flex items-center gap-2 text-gray-400">
                  <p className="text-sm italic">Cum ți-a fost ziua? 😊</p>
                </div>
              )}

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                <span className="text-xs text-purple-600 font-medium">
                  {todayNote ? 'Editează detalii' : 'Adaugă detalii'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/journal/notes-history');
                  }}
                  className="text-xs text-gray-400 flex items-center gap-1 hover:text-gray-600"
                >
                  Vezi zilele cu notițe
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Notes list */}
            {notesLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-4">📝</div>
                <p className="text-gray-500 font-medium">Nicio notiță încă</p>
                <p className="text-gray-400 text-sm mt-1">
                  Apasă pe cardul de mai sus pentru a adăuga prima notiță
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Notițe anterioare
                </p>
                <div className="space-y-3">
                  {notes
                    .filter((n) => n.date.split('T')[0] !== today)
                    .map((note) => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        onEdit={handleEditNote}
                        onDelete={handleDeleteNote}
                      />
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => activeTab === 'transit' ? setShowAddBowel(true) : handleAddNote()}
        className="fixed bottom-24 right-4 w-14 h-14 bg-purple-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-purple-700 active:scale-95 transition-all z-40"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Modals */}
      <AddBowelModal
        isOpen={showAddBowel}
        onClose={() => setShowAddBowel(false)}
        onSuccess={loadBowelLogs}
      />

      <NoteModal
        isOpen={showNoteModal}
        onClose={() => { setShowNoteModal(false); setEditingNote(null); }}
        onSuccess={loadNotes}
        existingNote={editingNote}
        date={editingNote ? editingNote.date.split('T')[0] : today}
      />
    </div>
  );
}
